import React, {
  ReactElement,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { Link } from 'react-router-dom'
import * as CONFIG from '../../../../../config/'
import ExportReportShared from '../../../../../components/UI/ExportReportShared/ExportReportShared'
import { useTranslation } from 'react-i18next'
import { Box, Typography } from '@mui/material'
import { DataGrid, GridColDef } from '@mui/x-data-grid'
import dayjs from 'dayjs'
import DashboardService from '@/services/DashboardService'
import queryString from 'query-string'

interface Quiz {
  pending_count: number
  average_score: number
  lowest_score: number
  highest_score: number
  total_score: number
  quiz_id: string
  quiz_name: string
  quiz_score: number
  quiz_type: string
  review_count: number
  submission_count: number
  last_submitted: string
  last_score: number
}

const dashboardService = new DashboardService()

export default function QuizSummary(props: any): ReactElement {
  const { t } = useTranslation('translation')

  const [paginationState, setPaginationState] = useState(null)
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  })

  const rowCountRef = useRef(paginationState?.total_items || 0)

  const rowCount = useMemo(() => {
    if (paginationState?.total_items !== undefined) {
      rowCountRef.current = paginationState.total_items
    }
    return rowCountRef.current
  }, [paginationState?.total_items])

  const [quizzes, setQuizzes] = useState<Quiz[]>([])
  const [loading, setLoading] = useState(false)

  const columns: GridColDef<Quiz>[] = [
    {
      field: 'quiz_name',
      headerName: t('Name'),
      width: 300,
      sortable: true,
      renderCell: params => {
        const { row } = params
        const query = queryString.stringify(
          {
            name: row.quiz_name,
            fromdate: props.filtersData.filter_from_date,
            todate: props.filtersData.filter_to_date,
          },
          { skipNull: true, skipEmptyString: true },
        )
        return (
          <Link
            to={
              CONFIG.QUIZZDETAILSBYID.replace(':quizId', row.quiz_id).replace(
                ':quizType',
                row?.quiz_type === 'roleplay' ? 'roleplay' : 'quiz',
              ) + `?${query}`
            }
            className="hover:tw-no-underline"
          >
            <Typography variant="body3Link">{row.quiz_name || '-'}</Typography>
          </Link>
        )
      },
    },
    {
      headerName: t('No of Submissions'),
      field: 'submission_count',
      sortable: true,
      width: 190,
    },
    {
      type: 'date',
      headerName: t('Last Submitted'),
      field: 'last_submission_at',
      sortable: true,
      valueFormatter: (value?: string) =>
        dayjs.utc(value).local().format('DD-MM-YYYY'),
      width: 150,
    },
    {
      type: 'number',
      headerName: t('Average Score'),
      field: 'average_score',
      sortable: true,
      valueFormatter: (value?: number) => {
        if (value == null) {
          return ''
        }
        return value.toFixed(2)
      },
      width: 150,
    },
    {
      headerName: t('No of Reviews'),
      field: 'review_count',
      sortable: true,
      width: 150,
    },
    {
      headerName: t('Pending Reviews'),
      field: 'pending_count',
      sortable: true,
      width: 120,
    },
  ]

  const fun = async () => {
    setLoading(true)
    try {
      const data = await dashboardService.getDashboardQuizList({
        ...props.filtersData,
        page: paginationModel.page + 1,
        page_size: paginationModel.pageSize,
      })
      setQuizzes(data?.data?.data?.report_data?.rows)
      setPaginationState(data?.data?.data?.report_data?.pagination)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (
      loading ||
      !props.filtersData.filter_from_date ||
      !props.filtersData.filter_to_date
    ) {
      return
    }

    fun()
  }, [props.filtersData, paginationModel])

  return (
    <div className="tw-border tw-rounded-2xl tw-shadow-md tw-p-8">
      <div className="tw-flex tw-items-center tw-justify-between tw-mb-4">
        <Typography variant="subtitle2" component="h2">
          {t('Quizzes')}
        </Typography>
        {!loading && (
          <ExportReportShared
            name="quiz-list"
            filtersData={props.filtersData}
          />
        )}
      </div>
      <Box sx={{ height: 400, width: '100%' }}>
        <DataGrid
          getRowId={row => row.quiz_id}
          rows={quizzes}
          columns={columns}
          rowCount={rowCount}
          loading={loading}
          paginationModel={paginationModel}
          paginationMode="server"
          onPaginationModelChange={setPaginationModel}
        />
      </Box>
    </div>
  )
}
